/**
 * Director (chat) mode entry gate — Jul 20 2026.
 *
 * New entry points into Director chat are hidden for now: the composer mode
 * pickers on Home and the music-video generator no longer offer it, sticky
 * composer drafts are coerced back to Studio, and the Studio stepper no
 * longer offers a mid-project switch INTO chat. Existing chat-mode projects
 * keep working unchanged, and chat -> Studio switching stays available.
 *
 * Re-enable by setting VITE_DIRECTOR_MODE_ENTRY=true.
 */
export const directorModeEntryEnabled =
  import.meta.env.VITE_DIRECTOR_MODE_ENTRY === "true";

/**
 * Power users who keep Director (chat) entry while it is gated for everyone
 * else. They drove their whole workflow through chat before the Jul 20
 * gating; taking the entry away strands them on projects they know how to
 * run. Everyone else still sees only Studio/Lite until the env flag flips.
 */
const DIRECTOR_MODE_ENTRY_USER_ALLOWLIST = new Set<string>([
  "113902195692153761765", // James / MetronomiX (SUP-L1UMWATE) — chat power user
  "email_16073146c402119ba18cfb5488eded96", // Dan Olds (oursalvation@msn.com) — Sep 4 2026, per-shot edit_shot fixes via chat
]);

/**
 * Whether the Director (chat) entry points are visible for this user. The
 * director_default_v1 "director" arm (auth.me's directorDefaultArm, see
 * shared/directorDefaultExperiment.ts) gets entry too.
 */
export function directorModeEntryEnabledFor(
  userId?: string | null,
  directorDefaultArm?: string | null,
): boolean {
  return (
    directorModeEntryEnabled ||
    directorDefaultArm === "director" ||
    (!!userId && DIRECTOR_MODE_ENTRY_USER_ALLOWLIST.has(String(userId)))
  );
}

/**
 * The mode a new music-video project starts in when the user hasn't picked
 * one: Director chat for the director_default_v1 "director" arm, the Studio
 * stepper for everyone else.
 */
export function defaultNewProjectModeFor(
  directorDefaultArm?: string | null,
): "studio" | "fast_v2" {
  return directorDefaultArm === "director" ? "studio" : "fast_v2";
}

/** Coerce any new-project mode: sticky or programmatic Director entries fall
 * back to the step-by-step studio while chat entry is gated. */
export function gateNewProjectMode<T extends string | undefined | null>(
  mode: T,
  entryEnabled = directorModeEntryEnabled,
): T | "fast_v2" {
  if (!entryEnabled && mode === "studio") return "fast_v2";
  // Lite ("fast", the legacy automatic 15s clip) was retired from the web
  // composers on Aug 22 2026: 0.8% of projects, $0 from Lite-only users in
  // the prior 30 days. Existing "fast" projects keep working; new ones
  // always start in Studio.
  if (mode === "fast") return "fast_v2";
  return mode;
}

/**
 * Whether the composer shows the project-mode picker at all. Since the Dance
 * mode (Sep 2026) everyone has at least two options (Studio + Dance), so the
 * picker is always meaningful; Director chat entry stays gated separately
 * via directorModeEntryEnabledFor.
 */
export function projectModePickerVisibleFor(_userId?: string | null): boolean {
  return true;
}

export function gateDraftProjectMode<T extends string | undefined | null>(
  mode: T,
  entryEnabled = directorModeEntryEnabled,
): T | "fast_v2" {
  return gateNewProjectMode(mode, entryEnabled);
}
